import { Currency } from "@redotech/money/currencies";
import {
  addWeight,
  convertWeight,
  Weight,
  WeightSchema,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum HSCodeSource {
  CLASSIFICATION = "CLASSIFICATION",
  MARKETPLACE = "MARKETPLACE",
  MANUAL = "MANUAL",
  CHECKOUT_QUOTE = "CHECKOUT_QUOTE", // Just zonos for now, but could come from any source
}

// Mirrors ManufacturerRecord.address (product-customs-facts.ts), which lives in a
// service package this common module can't depend on. Kept structurally identical.
const ManufacturerAddressSchema_ = z.object({
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }).nullish(),
});
export const ManufacturerAddressSchema: z.ZodType<ManufacturerAddress> =
  ManufacturerAddressSchema_;
export interface ManufacturerAddress extends z.infer<
  typeof ManufacturerAddressSchema_
> {}

const CustomsDeclarationSchema_ = z.object({
  description: z.string(),
  quantity: z.number(),
  itemValue: z.number(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  itemWeight: WeightSchema,
  harmonizationCode: z.string().nullish(),
  hsCodeSource: createEnum(HSCodeSource, {
    enumName: "HSCodeSource",
  }).nullish(),
  countryOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }),
  // Manufacturer company / contact name.
  manufacturer: z.string().nullish(),
  // Manufacturer Identification Number (MID); some carriers (e.g. Chit Chats
  // U.S. DDP) accept it and otherwise auto-generate one.
  manufacturerMid: z.string().nullish(),
  manufacturerAddress: ManufacturerAddressSchema_.nullish(),
  productIdentifier: z.string().nullish(),
});
export const CustomsDeclarationSchema: z.ZodType<CustomsDeclaration> =
  CustomsDeclarationSchema_;
export interface CustomsDeclaration extends z.infer<
  typeof CustomsDeclarationSchema_
> {}
export interface ICustomsDeclaration extends z.infer<
  typeof CustomsDeclarationSchema_
> {}

export function customsDeclarationsAreEqual({
  a,
  b,
}: {
  a?: CustomsDeclaration | null;
  b?: CustomsDeclaration | null;
}) {
  if (!a || !b) {
    return false;
  }

  return (
    a.description === b.description &&
    a.quantity === b.quantity &&
    a.itemValue === b.itemValue &&
    a.manufacturer == b.manufacturer &&
    a.manufacturerMid == b.manufacturerMid &&
    manufacturerAddressesAreEqual(
      a.manufacturerAddress,
      b.manufacturerAddress,
    ) &&
    a.productIdentifier == b.productIdentifier &&
    convertWeight(a.itemWeight, WeightUnit.OUNCE).value ===
      convertWeight(b.itemWeight, WeightUnit.OUNCE).value &&
    a.harmonizationCode === b.harmonizationCode &&
    a.countryOfOrigin === b.countryOfOrigin
  );
}

function manufacturerAddressesAreEqual(
  a?: ManufacturerAddress | null,
  b?: ManufacturerAddress | null,
): boolean {
  return (
    (a?.street1 ?? null) === (b?.street1 ?? null) &&
    (a?.street2 ?? null) === (b?.street2 ?? null) &&
    (a?.city ?? null) === (b?.city ?? null) &&
    (a?.state ?? null) === (b?.state ?? null) &&
    (a?.postalCode ?? null) === (b?.postalCode ?? null) &&
    (a?.country ?? null) === (b?.country ?? null)
  );
}

export function getCustomsDeclarationsTotalWeight(
  customsDeclarations: CustomsDeclaration[],
): Weight {
  return customsDeclarations.reduce(
    (total: Weight, declaration) => {
      if (declaration.quantity > 0) {
        return addWeight(total, {
          unit: WeightUnit.OUNCE,
          value: declaration.itemWeight.value * declaration.quantity,
        });
      }
      return total;
    },
    { unit: WeightUnit.OUNCE, value: 0 },
  );
}
